import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { TaskService } from '../../services/task.service';
import { BatchService } from '../../services/batch.service';
import { BatchDocumentProcessDiagramComponent } from '../batch-document-process-diagram/batch-document-process-diagram.component';
import { TaskPropertiesComponent } from '../task-properties/task-properties.component';

/**
 * Component for viewing the properties of the set of documents 
 * contained in a workflow batch.
 */
@Component({
	selector: 'workflow-batch-documents',
	templateUrl: './batch-documents.component.html',
	styleUrl: './batch-documents.component.css'
})
export class BatchDocumentsComponent implements OnInit {
	@Input() batchId: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	batch: any = {};
	downloadJobTypes: any[] = [];

	constructor(
		private modalService: NgbActiveModal,
		private ngbModal: NgbModal,
		private alertsService: AlertsService,
		private userService: UserService,
		private taskService: TaskService,
		private batchService: BatchService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.downloadJobTypes = [...this.batchService.downloadJobTypes];
		this.loadFullBatch();
	}

	private loadFullBatch(): void {
		this.batchService.getBatch(this.batchId).then(
			(response: any) => {
				this.batch = response || {};
			},
			(err: any) => {
				console.error("Failed to load batch", err);
				this.alertsService.error({
					message: this.LOCALIZATION.batchLoadError
				});
			}
		);
	}

	downloadResults(jobId: number, title: string, downloadJobType: any): void {
		this.batchService.downloadJobResults(jobId, downloadJobType).then(
			(response: any) => {
				let blob = new Blob([response.data], { type: "application/octet-stream" });
				let downloadName = `${title}-${downloadJobType.id}.${downloadJobType.format}`;

				let link = document.createElement('a');
				link.style.display = 'none';
				let url = window.URL.createObjectURL(blob);
				link.href = url;
				link.download = downloadName;
				document.body.appendChild(link);
				link.click();

				setTimeout(() => {
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);
				}, 100);
			},
			(err: any) => {
				console.error("Failed to download results", err);
				this.alertsService.error({
					message: this.LOCALIZATION.downloadError
				});
			}
		);
	}

	/**
	 * Opens the workflow process diagram for the document task.
	 */
	openProcessDiagram(pid: number, taskName?: string): void {
		const modalRef = this.ngbModal.open(BatchDocumentProcessDiagramComponent, {
			size: 'lg'
		});

		modalRef.componentInstance.processData = { processId: pid, subtitle: taskName || this.batch.batchName };

		modalRef.result.then(
			(result) => {
				console.log('Modal dismissed');
			},			
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays a view of the document task properties.
	 */
	showTaskProperties(taskid: number): void {
		this.taskService.getTask(taskid).then(
			(response: any) => {
				if (response) {
					this.loadTaskPropertiesModal(response);
				}
				else {
					console.log("Task properties not found.");
					this.alertsService.error({
						message: "No task properties found."
					});
				}
			},
			(err: any) => {
				console.error("Failed to load task", err);
				this.alertsService.error({
					message: this.LOCALIZATION.taskLoadError
				});
			}
		);
	}

	private loadTaskPropertiesModal(task: any): void {
		const modalRef = this.ngbModal.open(TaskPropertiesComponent, {
			size: 'lg'
		});

		modalRef.componentInstance.task = task;

		modalRef.result.then(
			(result) => {
				console.log('Modal dismissed');
			},			
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.modalService.dismiss();
	}
}
